import React, { useState, useEffect, useCallback } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { 
  BrainCircuit,
  Loader2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext'; 
import PremiumBadge from '../components/PremiumBadge'; 

export default function InformatrixPage() {
  const { currentUser } = useAuth(); 
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = currentUser?.is_premium || false;

  const fetchMetrics = useCallback(async () => {
    try {
      if (!metrics) setLoading(true); 
      const response = await api.get('/performance/metrics');
      setMetrics(response.data);
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
    } finally {
      setLoading(false);
    }
  }, [metrics]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);
  
  const containerRef = usePullToRefresh(fetchMetrics);

  const getSeverityStyle = (severity) => {
    switch(severity) {
      case 'CRITICAL': 
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300';
      case 'HIGH': 
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-500/30 text-orange-700 dark:text-orange-300';
      case 'MODERATE': 
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-500/30 text-yellow-700 dark:text-yellow-300';
      default: 
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-800 dark:text-white text-lg">Analyzing matrix...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-500 mb-4">Could not load performance data.</p>
          <button onClick={() => fetchMetrics()} className="text-blue-500 underline">Retry</button>
        </div>
      </div>
    );
  }

  const detectedBehaviors = (metrics.raw_behaviors || []).filter(b => b.detected && b.severity !== 'NONE');

  return (
    <div 
      ref={containerRef}
      className="h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 pb-20 transition-colors duration-300 overflow-y-auto overflow-x-hidden"
    >
      
      {/* Sticky compact bar — title and description scroll beneath it */}
      <header className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Informatrix</h1>
        </div>
      </header>

      <div className="pt-4 pb-4 mb-2 max-w-3xl mx-auto">
        <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
          Informatrix
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          Trade-pattern indicators from your recent trade history. These are prompts for reflection, not psychological diagnoses.
        </p>
      </div>

      {metrics.analysis_quality?.status === 'insufficient_data' && (
        <div className="max-w-3xl mx-auto mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <strong>Not enough evidence to assess patterns.</strong> The rules need at least {metrics.analysis_quality.minimum_sample_size} closed trades in the last {metrics.analysis_quality.window_days} days; this sample has {metrics.analysis_quality.sample_size}. No behavior finding is inferred from a smaller sample.
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        
        {/* MAIN CARD - Added mb-10 for space below the card */}
        <div className="relative bg-white dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-lg mb-10">
          
          {/* 1. Profile Header & Stats Strip */}
          <div className="p-5 border-b border-slate-100 dark:border-slate-700">
             <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                        {metrics.username || 'Trader'}
                    </h2>
                    {isPremium && <PremiumBadge size={14} />}
                </div>
                <span className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 px-2 py-0.5 rounded-full font-medium uppercase">
                    {metrics.time_range}
                </span>
             </div>

             {/* Direct Stats Strip */}
             {metrics.stats && (
                <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1 font-semibold text-green-600 dark:text-green-400">
                        <Target size={12} /> {metrics.stats.win_rate}% Win
                    </span>
                    <span className={`flex items-center gap-1 font-semibold ${metrics.stats.net_profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                        {metrics.stats.net_profit >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />} 
                        ${metrics.stats.net_profit.toFixed(2)}
                    </span>
                    <span className="flex items-center gap-1">
                        <Activity size={12} /> {metrics.stats.total_trades} Trades
                    </span>
                </div>
             )}
          </div>

          {/* Evidence and context instead of a pseudo-psychological grade */}
          <div className="p-5 border-b border-slate-100 dark:border-slate-700 bg-blue-50/30 dark:bg-slate-900/30">
             <div className="flex items-start gap-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-500">
                   <BrainCircuit size={20} />
                </div>
                <div className="flex-1">
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase mb-2">Evidence standard</p>
                    <p className="text-xs leading-5 text-slate-600 dark:text-slate-300">A behavior is shown only when at least two distinct rule indicators support it, using closed trades from the last 90 days. These are review prompts, not diagnoses or predictions.</p>
                </div>
             </div>
          </div>

          {/* 4. Behavioral Matrix (Grid Layout) */}
          {detectedBehaviors.length > 0 && (
            <div className="p-5 border-b border-slate-100 dark:border-slate-700">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-orange-500" /> Active Behaviors ({detectedBehaviors.length})
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {detectedBehaviors.map((behavior, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg border ${getSeverityStyle(behavior.severity)}`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-[11px] uppercase tracking-wide">{behavior.behavior}</span>
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-white/50 dark:bg-black/20">
                        {behavior.severity}
                      </span>
                    </div>
                    <p className="text-[11px] leading-snug opacity-90">{behavior.message}</p>
                    {behavior.evidence?.length > 0 && (
                      <ul className="mt-2 space-y-1 border-t border-current/10 pt-2 text-[10px]">
                        {behavior.evidence.map((evidence) => (
                          <li key={evidence.indicator}>
                            <strong>{evidence.indicator}:</strong> observed {typeof evidence.observed === 'number' ? Number(evidence.observed.toFixed(3)) : evidence.observed}; threshold {evidence.threshold}. {evidence.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {detectedBehaviors.length === 0 && metrics.analysis_quality?.status === 'ready' && (
            <p className="border-b border-slate-100 p-5 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">No pattern met the evidence threshold in this sample. That does not establish the absence of a pattern.</p>
          )}

        </div>
      </div>
    </div>
  );
}
